import { useState, useEffect } from "react";
import axios from "axios";
import { Navbar, Footer } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Banknote, Calendar, Megaphone, Gift, Newspaper, Clock } from "lucide-react";
import API from "@/utils/api";

const Activities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/activities`);
      setActivities(response.data);
    } catch (error) {
      console.error("Error fetching activities:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "schemes": return <Banknote className="w-5 h-5" />;
      case "events": return <Calendar className="w-5 h-5" />;
      case "news": return <Megaphone className="w-5 h-5" />;
      case "lucky_draw": return <Gift className="w-5 h-5" />;
      default: return <Newspaper className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "schemes": return "bg-green-100 text-green-700";
      case "events": return "bg-blue-100 text-blue-700";
      case "news": return "bg-amber-100 text-amber-700";
      case "lucky_draw": return "bg-purple-100 text-purple-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  return (
    <div className="min-h-screen bg-transparent" data-testid="activities-page">
      <Navbar />
      <div className="pt-24 pb-16 max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full mb-4">
            <Newspaper className="w-5 h-5" />
            <span className="font-medium">Daily Updates</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">Activities & News</h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            Stay updated with the latest news, government schemes, events, and activities for women empowerment.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-terracotta"></div>
          </div>
        ) : activities.length === 0 ? (
          <Card className="max-w-lg mx-auto rounded-2xl text-center p-8">
            <Newspaper className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Activities Yet</h3>
            <p className="text-slate-500">Check back soon for updates on women activities and schemes.</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activities.map((activity) => (
              <Card key={activity.id} className="rounded-2xl overflow-hidden hover:shadow-lg transition-shadow" data-testid={`activity-${activity.id}`}>
                {activity.image_url && (
                  <div className="aspect-video bg-slate-100 overflow-hidden">
                    <img 
                      src={activity.image_url.startsWith('data:') ? activity.image_url : `/images/${activity.image_url}`} 
                      alt={activity.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={getCategoryColor(activity.category)}>
                      {getCategoryIcon(activity.category)}
                      <span className="ml-1 capitalize">{activity.category}</span>
                    </Badge>
                  </div>
                  <CardTitle className="text-lg">{activity.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 text-sm">{activity.description}</p>
                  <p className="text-xs text-slate-400 mt-3">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {new Date(activity.created_at).toLocaleDateString()}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Activities;
